import { SafeClientResult } from '@safe-global/sdk-starter-kit'
import { SafeClientTxStatus } from '@safe-global/sdk-starter-kit/dist/src/constants'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { useToastManager } from 'src/common/components/ToastManager/hooks/useToastManager'
import { resolveAllThirdwebTransactions } from 'src/common/utils/web3/resolveThirdwebTransaction'
import { useState } from 'react'
import { PreparedTransaction, waitForReceipt } from 'thirdweb'
import { useActiveAccount, useActiveWalletChain } from 'thirdweb/react'
import { getExplorerUrl } from 'src/common/utils/web3/getExplorerUrl'
import Safe, {
  buildSignatureBytes,
  EthSafeSignature,
  SigningMethod,
} from '@safe-global/protocol-kit'
import { executeProposedTransaction } from 'src/common/api-clients/safe-protocol-kit/safe-multisign'
import { thirdwebClient } from 'src/common/api-clients/thirdweb'

type BatchTxsStatus =
  | 'idle'
  | 'loading'
  | 'error'
  | 'success'
  | SafeClientTxStatus

const useSendBatchTxs = () => {
  const account = useActiveAccount()
  const { smartAccount, smartAccountInfo } = useSmartAccount()
  const { addToast } = useToastManager()
  const chain = useActiveWalletChain()
  const [data, setData] = useState<SafeClientResult>()
  const [status, setStatus] = useState<BatchTxsStatus>('idle')

  const sendBatchTxs = async (txs: PreparedTransaction[]) => {
    if (!smartAccount || !smartAccountInfo?.address || !account?.address) return
    try {
      setStatus('loading')
      const resolvedTxs = await resolveAllThirdwebTransactions(txs)
      resolvedTxs[0].value = resolvedTxs[0].value || '0' // fallback initial value to ZERO cuz due to thirdweb provider error: "value cannot be empty"

      const safeTx = await smartAccount?.protocolKit.createTransaction({
        transactions: resolvedTxs,
        options: {
          safeTxGas: '800000',
        },
      })
      const signedTx = await smartAccount.protocolKit.signTransaction(
        safeTx!,
        SigningMethod.ETH_SIGN
      )
      const signature = await signedTx.getSignature(account.address)

      const executedTxRes = await executeProposedTransaction({
        ownerAddress: account.address,
        safeAddress: smartAccountInfo.address,
        chainId: 137,
        signature: buildSignatureBytes([signature!]),
        ...safeTx.data,
      })
      const executedTx = executedTxRes.data

      const receipt = await waitForReceipt({
        client: thirdwebClient,
        chain: chain!,
        transactionHash: executedTx.hash! as `0x${string}`,
      })

      setStatus('success')
      addToast('Transaction sent successfully', {
        type: 'success',
        actionHref: getExplorerUrl({
          chainId: chain?.id,
          hash: executedTx?.hash,
          type: 'tx',
        }),
        actionText: 'View',
      })

      setData({
        status: SafeClientTxStatus.EXECUTED,
        transactions: {
          ethereumTxHash: executedTx?.hash,
        },
        description: '',
        safeAddress: await smartAccount?.protocolKit.getAddress()!,
      })
      return receipt
    } catch (error) {
      setStatus('error')
      if (error instanceof Error) {
        addToast(error.message || 'Transaction failed', {
          type: 'error',
        })
      }
      console.log(error)
    }
  }

  return {
    sendBatchTxs,
    data,
    status,
    isPending: status == 'loading',
  }
}

export default useSendBatchTxs
